/**
 * GLAMRDiP search panel
 * - Opens on gd:search-open event
 * - Debounced predictive-search queries to /search/suggest.json
 * - Renders products + collections + pages + articles in the grouped results
 * - Full-form submit on Enter goes to /search?q= (Shopify handles)
 *
 * Joel 2026-05-02 (architecture fix):
 *   /search/suggest.json bypasses sections/predictive-search.liquid entirely —
 *   that template's exclusion logic never runs on this surface. Mirror the
 *   filters client-side here, reading config from [data-gd-search-panel-config]
 *   (emitted by sections/search-panel.liquid). Also enforces two hard rules
 *   that aren't customizer-controlled:
 *     1. Hide ALL pages from search results (per Joel)
 *     2. Hide all $0 products (kit-colour duplicates / bundle children)
 */
(() => {
  const panel = document.querySelector('[data-gd-search]');
  if (!panel) return;

  const form = panel.querySelector('[data-gd-search-form]');
  const input = panel.querySelector('#gd-search-input');
  const results = panel.querySelector('[data-gd-search-results]');
  const landing = panel.querySelector('[data-gd-search-landing]');
  const empty = panel.querySelector('[data-gd-search-empty]');
  const productTpl = panel.querySelector('[data-gd-search-product-template]');
  const linkTpl = panel.querySelector('[data-gd-search-link-template]');

  // ── Exclusion config — read from JSON block emitted by Liquid ──────
  function getExclusionConfig() {
    const el = document.querySelector('[data-gd-search-panel-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[gd-search-panel] exclusion config parse failed:', e && e.message);
      return null;
    }
  }
  const excludeCfg = getExclusionConfig();

  // Apply customizer + hard-rule filters to /search/suggest.json results.
  // Hard rules: pages always hidden, $0 products always hidden, blanket-block
  // bundle-component-only / gift-only tagged products (mirrors the
  // server-side guards in main-search.liquid + predictive-search.liquid).
  function filterResults(rawResults) {
    const out = {
      products: [],
      collections: [],
      // Hard rule: pages NEVER appear in search results.
      pages: [],
      articles: rawResults.articles || [],
    };
    const cfg = excludeCfg || {};
    const exTags = (cfg.excluded_tags || []).map((t) => String(t).toLowerCase());
    const exPids = (cfg.excluded_product_ids || []).map((id) => String(id));
    const exColResults = (cfg.hidden_collection_result_handles || []);

    // Products
    for (const p of (rawResults.products || [])) {
      // /search/suggest.json returns price as a STRING in dollar units
      // (e.g. "15.00"). Hard rule: any $0 product is hidden.
      const priceNum = typeof p.price === 'string' ? parseFloat(p.price) : Number(p.price || 0);
      if (!isFinite(priceNum) || priceNum === 0) continue;

      // Static guards (mirror main-search.liquid lines 62-67)
      const productTags = Array.isArray(p.tags)
        ? p.tags.map((t) => String(t).toLowerCase())
        : (typeof p.tags === 'string' ? p.tags.split(',').map((t) => t.trim().toLowerCase()) : []);
      if (productTags.includes('bundle-component-only') || productTags.includes('gift-only')) continue;

      // Customizer: explicit product id exclusion
      if (exPids.length && exPids.includes(String(p.id))) continue;

      // Customizer: tag-based exclusion (any-match)
      if (exTags.length && exTags.some((t) => productTags.includes(t))) continue;

      // Note: collection-membership exclusion (cfg.excluded_collection_handles)
      // can't be applied here because /search/suggest.json product objects
      // don't include the collections array. Falls through to /search HTML
      // page where the Liquid filter still applies.

      out.products.push(p);
    }

    // Collections — hide if handle in customizer's "Hide as COLLECTION results"
    for (const c of (rawResults.collections || [])) {
      if (exColResults.length && exColResults.includes(c.handle)) continue;
      out.collections.push(c);
    }

    return out;
  }

  const setView = (mode) => {
    // mode: 'landing' | 'empty' | 'results'
    if (landing) landing.hidden = mode !== 'landing';
    if (empty)   empty.hidden   = mode !== 'empty';
  };
  let abortCtrl = null;
  let debounceTimer = null;

  const open = () => {
    panel.hidden = false;
    if (window.gdScrollLock) window.gdScrollLock.lock(panel);
    else document.body.style.overflow = 'hidden';
    setTimeout(() => input?.focus(), 50);
  };
  const close = () => {
    panel.hidden = true;
    if (window.gdScrollLock) window.gdScrollLock.unlock(panel);
    else document.body.style.overflow = '';
  };

  document.body.addEventListener('gd:search-open', open);
  panel.querySelectorAll('[data-gd-search-close]').forEach(b => b.addEventListener('click', close));
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && !panel.hidden) close(); });

  // Joel 2026-05-02: /search/suggest.json returns price as a STRING in dollar
  // units (e.g. "15.00"), NOT integer cents. Previous code did `p / 100` on
  // a string and produced "$0.15" instead of "$15.00" for Blackout. parseFloat
  // for strings; for numeric input (just in case), coerce + treat as dollars.
  const fmtPrice = (p) => {
    const num = typeof p === 'string' ? parseFloat(p) : Number(p || 0);
    const safe = isFinite(num) ? num : 0;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: safe % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(safe);
  };

  const render = (data) => {
    // Clear old results
    [...results.querySelectorAll('.gd-search__result')].forEach(n => n.remove());
    const products = data?.resources?.results?.products || [];
    const collections = data?.resources?.results?.collections || [];
    const pages = data?.resources?.results?.pages || [];
    const articles = data?.resources?.results?.articles || [];
    const total = products.length + collections.length + pages.length + articles.length;
    if (total === 0) {
      if (input.value) { empty.textContent = 'Ingen match.'; setView('empty'); }
      else setView('landing');
      input.setAttribute('aria-expanded', 'false');
      return;
    }
    setView('results');
    input.setAttribute('aria-expanded', 'true');

    products.slice(0, 6).forEach(p => {
      const node = productTpl.content.firstElementChild.cloneNode(true);
      node.href = p.url;
      node.querySelector('[data-gd-search-title]').textContent = p.title;
      node.querySelector('[data-gd-search-price]').textContent = fmtPrice(p.price);
      const img = node.querySelector('[data-gd-search-img]');
      if (p.featured_image?.url || p.image) {
        img.src = p.featured_image?.url || p.image;
        img.alt = p.title;
      } else {
        img.remove();
      }
      results.appendChild(node);
    });

    const appendLinks = (items, kind) => items.slice(0, 4).forEach(x => {
      const node = linkTpl.content.firstElementChild.cloneNode(true);
      node.href = x.url;
      node.querySelector('[data-gd-search-kind]').textContent = kind;
      node.querySelector('[data-gd-search-title]').textContent = x.title;
      results.appendChild(node);
    });
    appendLinks(collections, 'Collection');
    appendLinks(pages, 'Page');
    appendLinks(articles, 'Article');
  };

  const query = async (q) => {
    if (!q || q.length < 2) {
      [...results.querySelectorAll('.gd-search__result')].forEach(n => n.remove());
      setView('landing');
      return;
    }
    if (abortCtrl) abortCtrl.abort();
    abortCtrl = new AbortController();
    try {
      const r = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=hide`,
        { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: abortCtrl.signal }
      );
      if (r.ok) {
        const json = await r.json();
        const filtered = filterResults(json.resources?.results || {});
        render({ resources: { results: filtered } });
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('search error', e);
    }
  };

  input?.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => query(input.value.trim()), 200);
  });

  form?.addEventListener('submit', (e) => {
    // Let the browser do a plain GET to /search?q=... which Shopify handles natively
    if (!input.value.trim()) e.preventDefault();
  });
})();
